/**
 * Hue Scene Config Sync Service
 * Listens to scene_light_configs changes and applies them to physical lights and lights table
 */

import { getDataProvider, isStandaloneMode, TABLES, type Subscription } from '@/lib/dataProvider'
import { hueBridgeService } from './HueBridgeService'
import { hueSyncCoordinator } from './HueSyncCoordinator'
import { useScenesStore } from '@/stores/scenes'
import { useSceneLightConfigsStore } from '@/stores/sceneLightConfigs'
import { useLightsStore, type Light } from '@/stores/lights'
import { hueLogger } from '@/lib/logger'
import type { HueLightCommand } from './HueBridgeService'
import type { ResolvedSceneLightConfig } from '@/stores/sceneLightConfigs'

export interface SceneConfigSyncEvent {
  sceneId: string
  lightId: string
  configId: string
  targetState: Record<string, unknown>
  isInherited: boolean
  inheritedFrom?: string
}

export class HueSceneConfigSyncService {
  private subscription: Subscription | null = null
  private activeBridges = new Map<string, { id: string; ip: string }>()
  private isRunning = false
  private currentActiveSceneId: string | null = null

  /**
   * Start listening to scene config changes for the active scene
   */
  async start(): Promise<void> {
    if (this.isRunning) {
      hueLogger.warn('Scene config sync service already running')
      return
    }

    this.isRunning = true
    hueLogger.info('🎬 Starting scene config sync service')

    // Get active scene
    const scenesStore = useScenesStore()
    this.currentActiveSceneId = scenesStore.getActiveScene?.id || null

    if (!this.currentActiveSceneId) {
      hueLogger.info('ℹ️ No active scene - scene config sync will wait for scene activation')
    }

    // Load bridge information
    await this.loadBridgeInfo()

    // Start listening to scene config changes
    await this.startSceneConfigSync()

    hueLogger.success('✅ Scene config sync service started')
  }

  /**
   * Stop the sync service
   */
  async stop(): Promise<void> {
    if (!this.isRunning) return

    hueLogger.info('🛑 Stopping scene config sync service')
    this.isRunning = false

    // Clean up subscription
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    this.activeBridges.clear()
    this.currentActiveSceneId = null

    hueLogger.success('✅ Scene config sync service stopped')
  }

  /**
   * Load bridge information from database
   */
  private async loadBridgeInfo(): Promise<void> {
    try {
      const provider = getDataProvider()
      const bridges = await provider.query<{ bridge_id: string; ip_address: string }>(TABLES.HUE_BRIDGES, {
        filters: [
          { column: 'is_active', operator: 'eq', value: true }
        ]
      })

      for (const bridge of bridges || []) {
        this.activeBridges.set(bridge.bridge_id, {
          id: bridge.bridge_id,
          ip: bridge.ip_address
        })
      }

      hueLogger.info(`📡 Loaded ${this.activeBridges.size} active bridges for scene sync`)
    } catch (error) {
      hueLogger.error('Failed to load bridge info:', error)
    }
  }

  /**
   * Update the active scene being monitored
   */
  async updateActiveScene(sceneId: string | null): Promise<void> {
    if (this.currentActiveSceneId === sceneId) return

    hueLogger.info(`🎬 Active scene changed: ${this.currentActiveSceneId} → ${sceneId}`)
    this.currentActiveSceneId = sceneId

    // Restart subscription with new scene filter
    if (this.isRunning) {
      await this.startSceneConfigSync()
    }

    // Apply all configs for the new active scene immediately
    if (sceneId) {
      await this.applyAllConfigsForScene(sceneId)
    }
  }

  /**
   * Start listening to scene_light_configs changes
   * NOTE: Subscription disabled - configs now applied via presence broadcasts only
   */
  private async startSceneConfigSync(): Promise<void> {
    // Clean up existing subscription
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    // No longer subscribing to DB changes - presence handles real-time sync
    hueLogger.info('ℹ️ Scene config DB subscription disabled (using presence for sync)')
  }

  /**
   * Apply a single config to the physical bridge
   * Only applies if this session is the leader
   * NOTE: This is now called directly from presence sync, not from DB subscriptions
   */
  async applyConfigToBridge(resolvedConfig: ResolvedSceneLightConfig, light: Light): Promise<void> {
    try {
      // Check if we are the leader before syncing
      const isLeader = await hueSyncCoordinator.checkIsLeader()
      
      if (!isLeader) {
        hueLogger.debug(`⏭️ Skipping sync - not the leader`)
        return
      }

      hueLogger.info(`👑 Leader syncing ${light.name}`)

      const targetState = resolvedConfig.target_state

      if (!light.hue_bridge_id || !targetState) {
        hueLogger.debug(`⏭️ Skipping bridge sync - missing bridge ID or target state for light ${light.name}`)
        return
      }

      const bridge = this.activeBridges.get(light.hue_bridge_id)
      if (!bridge) {
        hueLogger.debug(`⏭️ Skipping bridge sync - bridge ${light.hue_bridge_id} not active`)
        return
      }

      // Build Hue command from target state - FIX COLOR MODE CONFLICTS
      const command: HueLightCommand = {
        on: targetState.on,
        transitiontime: 4 // 400ms - reduced for more responsive feel
      }

      // Add brightness first (if light is on)
      if (targetState.on || targetState.on === undefined) {
        if (targetState.brightness !== undefined && targetState.brightness !== null) {
          command.bri = targetState.brightness
        }

        // PRIORITY FIX: Check for color_temperature FIRST to determine mode
        // If CT is set, use CT mode ONLY (ignore any hue/sat/xy that might also exist)
        if (targetState.color_temperature !== undefined && targetState.color_temperature !== null) {
          // Color temperature mode (white light)
          command.ct = targetState.color_temperature
          hueLogger.debug(`🌡️ Using CT color mode for ${light.name}:`, targetState.color_temperature)
        } 
        // Otherwise, check for color mode properties
        else if (targetState.xy && Array.isArray(targetState.xy) && targetState.xy.length === 2) {
          // XY color mode (already in correct format)
          command.xy = targetState.xy
          hueLogger.debug(`🎨 Using stored XY color for ${light.name}:`, targetState.xy)
        } else if (targetState.hue !== undefined && targetState.hue !== null && targetState.saturation !== undefined && targetState.saturation !== null) {
          // Convert Hue/Saturation to XY for better compatibility
          const xy = this.convertHSToXY(targetState.hue, targetState.saturation)
          command.xy = xy
          hueLogger.debug(`🌈 Converted HS (${targetState.hue}, ${targetState.saturation}) to XY (${xy[0].toFixed(4)}, ${xy[1].toFixed(4)}) for ${light.name}`)
        }
      }

      hueLogger.debug(`🚀 Applying config to bridge for ${light.name}:`, command)

      const success = await hueBridgeService.setLightState(
        light.hue_bridge_id,
        bridge.ip,
        light.hue_light_id,
        command
      )

      if (success) {
        hueLogger.success(`✅ Successfully applied config to bridge for ${light.name}`)
        
        // WORKAROUND: For Hue Spot lights, send a follow-up command after a short delay
        if (light.name.toLowerCase().includes('spot')) {
          setTimeout(async () => {
            hueLogger.debug(`🔄 Sending follow-up command for Hue Spot: ${light.name}`)
            await hueBridgeService.setLightState(
              light.hue_bridge_id,
              bridge.ip,
              light.hue_light_id,
              command
            )
          }, 100) // 100ms delay
        }
      } else {
        hueLogger.warn(`⚠️ Bridge API returned false for ${light.name}`)
      }

    } catch (error) {
      hueLogger.error(`❌ Failed to apply config to bridge for light ${light.name}:`, error)
    }
  }

  /**
   * Determine color mode from target state
   */
  private determineColorMode(targetState: Record<string, unknown>): string {
    if (targetState.xy) return 'xy'
    if (targetState.hue !== undefined || targetState.saturation !== undefined) return 'hs'
    if (targetState.color_temperature) return 'ct'
    return 'ct' // Default fallback
  }

  /**
   * Apply all configs for a scene immediately (when scene becomes active)
   * Updates current_state for all lights so pips show correct positions
   * NOTE: No longer updates lights table - that's handled by leader's DB persistence
   */
  async applyAllConfigsForScene(sceneId: string): Promise<void> {
    try {
      hueLogger.info(`🎬 Applying all configs for scene ${sceneId}`)

      const configsStore = useSceneLightConfigsStore()
      const resolvedConfigs = configsStore.getResolvedConfigsForScene(sceneId)

      hueLogger.info(`📝 Found ${resolvedConfigs.length} resolved configs to apply`)

      const lightsStore = useLightsStore()
      
      for (const resolvedConfig of resolvedConfigs) {
        // Get light information
        const light = lightsStore.getLight(resolvedConfig.light_id)
        if (!light) {
          hueLogger.warn(`⚠️ Light not found for config: ${resolvedConfig.light_id}`)
          continue
        }
        
        // Update current_state so pips show correct position
        const targetState = resolvedConfig.target_state
        const stateUpdate: Record<string, unknown> = {
          on: targetState.on,
          bri: targetState.brightness
        }
        
        // Apply color mode
        if (targetState.color_temperature != null) {
          stateUpdate.ct = targetState.color_temperature
          stateUpdate.hue = undefined
          stateUpdate.sat = undefined
          stateUpdate.xy = undefined
        } else if (targetState.xy != null) {
          stateUpdate.xy = targetState.xy
          stateUpdate.ct = undefined
        } else if (targetState.hue != null && targetState.saturation != null) {
          stateUpdate.hue = targetState.hue
          stateUpdate.sat = targetState.saturation
          stateUpdate.ct = undefined
        }
        
        lightsStore.updateCurrentState(light.id, stateUpdate)
        
        // Apply to bridge immediately (leader only) - for Hue lights
        await this.applyConfigToBridge(resolvedConfig, light)
      }

      hueLogger.success(`✅ Applied all configs for scene ${sceneId}`)

    } catch (error) {
      hueLogger.error(`Failed to apply all configs for scene ${sceneId}:`, error)
    }
  }

  /**
   * Convert Hue/Saturation values to XY coordinates
   * Based on Philips Hue color conversion algorithm
   */
  private convertHSToXY(hue: number, saturation: number): [number, number] {
    // Normalize hue (0-65535) to 0-360 degrees
    const hueDegrees = (hue / 65535) * 360
    
    // Normalize saturation (0-254) to 0-1
    const satNormalized = saturation / 254
    
    // Convert to radians
    const hueRadians = (hueDegrees * Math.PI) / 180
    
    // Calculate XY using standard color space conversion
    // This uses a simplified conversion that works well for most Hue lights
    const x = (1 + satNormalized * Math.cos(hueRadians)) / 3
    const y = (1 + satNormalized * Math.sin(hueRadians)) / 3
    
    // Clamp to valid XY range (0-1)
    const clampedX = Math.max(0, Math.min(1, x))
    const clampedY = Math.max(0, Math.min(1, y))
    
    // Round to 4 decimal places for precision
    return [
      Math.round(clampedX * 10000) / 10000,
      Math.round(clampedY * 10000) / 10000
    ]
  }

  /**
   * Get sync status
   */
  getSyncStatus(): {
    isRunning: boolean
    activeScene: string | null
    activeBridges: string[]
  } {
    return {
      isRunning: this.isRunning,
      activeScene: this.currentActiveSceneId,
      activeBridges: Array.from(this.activeBridges.keys())
    }
  }
}

// Singleton instance
export const hueSceneConfigSyncService = new HueSceneConfigSyncService()